import jQuery from 'jquery';
import {inspect} from "util";
import Cookies from "js-cookie";
import Alerts from "./misc/alerts";
import PlainObject = JQuery.PlainObject;
import StackTrace from "stacktrace-js";
import {Spinner} from "./views/spinner";
import {Order} from "./bo/order";
import {User} from "./bo/user";
import {OrderHistory} from "./bo/order_history";
import {InventoryUse, Product} from "./bo/product";
import {Session} from "./bo/session";
import {JsonDate} from "./misc/types";
import {Attachment} from "./bo/attachment";
import {Uploader} from "./misc/images";
import {Checkout} from "./bo/checkout";
import {Activity} from "./bo/activity";
import {DeliveryConfig} from "./bo/delivery_config";
import build_config from "../../../server/build/wrb_build_config";
import {ChartData} from "./bo/chart_data";
import {GrapesTemplate, Website} from "./bo/website";
import {Asset} from "./bo/asset";
import {Collection} from "./bo/collection";
import {WebsiteVersion} from "./bo/website_version";
import {HideProducts} from "./bo/hide_products";
import {Inventory} from "./bo/inventory";
import {paramsToQueryString} from "./misc/utils";
import {HandwrittenWord} from "./bo/handwritten_word";

/**
 * @return true if error has been handled, false to permit normal error handling
 */
type ErrorCallback = (msg: any) => boolean;

export interface RequestOptions {
    silent?: boolean,
    isLogin?: boolean,
    noAuthRedirects?: boolean;
    errorCallback?: ErrorCallback,
    noCache?: boolean,
    cachePrefix?: string,
    browserCaching?: boolean,
    noClearCache?: boolean,
}

export type CacheClearListener = () => void;

export const CLEAR_CACHE = "clear_cache";

const VOLATILE = "volatile";

function encodePathComponent(x: string) : string {
    // Tomcat chokes on even url-encoded slashes in path components.
    // So as a workaround, we transport slashes as <something else>.
    // This means that we can't represent a the <something else> in a URL in this system,
    // but we are very unlikely to want to and it's a much smaller problem than not being able
    // to represent a slash...
    const y = x && x.replace ? x.replace(/\//g, "<<SLASH>>") : x;
    return encodeURIComponent(y);
}

export function getAppUrl() {
    const href = window.location.href;
    const index = href.indexOf("#");
    return index == -1 ? href : href.substr(0, index);
}

const PREVIEW_WEBSITE_ID_COOKIE = "website_preview_id";

export function getPreviewId() {
    const id = Cookies.get(PREVIEW_WEBSITE_ID_COOKIE);
    return id ? +id : undefined;
}

export function setPreviewId(id: number | undefined) {
    if (id) {
        Cookies.set(PREVIEW_WEBSITE_ID_COOKIE, String(id));
    } else {
        Cookies.remove(PREVIEW_WEBSITE_ID_COOKIE);
    }
}

/**
 * Models interactions with the server, handles caching
 */
export default class Server {

    /**
     * Singleton instance
     */
    private static INSTANCE: Server;

    static instance() {
        if (Server.INSTANCE == null) {
            Server.INSTANCE = new Server();
        }
        return Server.INSTANCE;
    }

    private resolveUser? : (value: User | PromiseLike<User>) => void;

    private user_ = new Promise<User>((resolve, reject) => {
        this.resolveUser = resolve;
    });

    public setUser(user: User) {
        this.resolveUser && this.resolveUser(user);
    }

    public getUser() : Promise<User> {
        return this.user_;
    }

    /**
     * Private cache of GET responses.
     */
    private cache : {
        [key: string] : Promise<any>
    } = {};

    public runtimeParams_ : Promise<PlainObject>;

    private runtimeParamsResolver?: (value: PlainObject | PromiseLike<PlainObject>) => void;

    private defaultOpts : RequestOptions = {
        noAuthRedirects: false
    };

    private cacheClearListeners: CacheClearListener[] = [];

    private constructor() {
        this.runtimeParams_ = new Promise((resolve, reject) => {
            this.runtimeParamsResolver = resolve;
            this.ping("params"); // side effect is to populate runtime params
        });

        this.runtimeParams_.then((params) => {
            if (params["wrb.client.noAuthRedirects"]) {
                this.defaultOpts.noAuthRedirects = true;
            }
        });
    }

    addCacheClearListener(l: CacheClearListener) {
        this.cacheClearListeners.push(l);
    }

    async getRuntimeParams() {
        return this.runtimeParams_;
    }

    /**
     * Set default request options
     * @param opts
     * @return previous default request options
     */
    setRequestOpts(opts: RequestOptions) : RequestOptions {
        const r = this.defaultOpts;
        this.defaultOpts = opts;
        return r;
    }

    ping(msg: string) : Promise<PingResult> {

        // @ts-ignore
        const clientVersion : PingResult = build_config;

        const l = calcLocalStorageSize();

        const data = {
            msg: msg,
            client: clientVersion,
            ls: l.size,
            lc: l.cnt
        };

        const ping_ = this.ajax({
            method: "GET",
            url: "api/ping",
            dataType: "json",
            data: data,
            contentType: "application/json; charset=utf-8",
        }, {
            silent: true
        });

        ping_.then((ping) => {
            // capture client-side params
            if (ping && ping.params) {
                this.runtimeParamsResolver && this.runtimeParamsResolver(ping.params);
            }
        });

        return ping_;
    }

    loginRemembered(hashUrl: string) : Promise<User | undefined> {
        return this.ajax({
            method: "GET",
            url: "api/login" + (hashUrl ? `?hash=${encodeURIComponent(hashUrl)}` : "")
        });
    }

    login(data: { username?: string, password?: string }) : Promise<User> {
        return this.ajax({
            method: "POST",
            url: "api/login",
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8",
        });
    }

    logout() : Promise<any> {
        return this.ajax({
            method: "POST",
            url: "api/logout",
            contentType: "application/json; charset=utf-8",
        });
    }

    getOrdersByQueryString(query: string, opts?: RequestOptions) : Promise<Order[]> {
        return this.ajax({
            url: "api/orders" + query
        }, opts);
    }

    getOrdersByDate(from: JsonDate, to: JsonDate, useDeliveryDate?: boolean) : Promise<Order[]> {
        const params : any = {
            from: from,
            to: to
        };
        if (useDeliveryDate) {
            params["useDeliveryDate"] = "true";
        }
        const query = paramsToQueryString(params);
        return this.ajax({
            url: "api/orders" + query
        });
    }

    getOrdersById(ids: string[]) : Promise<Order[]> {
        const params : any = {
            ids: ids
        };
        const query = paramsToQueryString(params);
        return this.ajax({
            url: "api/orders" + query
        });
    }
    getPickups() : Promise<Order[]> {
        return this.ajax({
            url: "api/orders/pickups",
        });
    }

    getOrder(id: string) : Promise<Order> {
        return this.ajax({
            url: `api/orders/${id}`,
        });
    }

    getOrdersByNumber(n: string) : Promise<Order[]> {
        return this.ajax({
            url: `api/number/${n}`,
        });
    }

    getOrdersByQRCode(qrcode: string) : Promise<Order[]> {
        const params : any = {
            qrcode: qrcode
        };
        const query = paramsToQueryString(params);
        return this.ajax({
            url: `api/qrcode` + query,
        });
    }

    getOrderImageUrl(id: string) : Promise<string | undefined> {
        return this.ajax({
            url: `api/orders/${id}/img`,
        });
    }

    getOrderNoAuth(id: string) : Promise<Order> {
        return this.ajax({
            url: `api/orders/${id}`,
        }, {
            noAuthRedirects: true,
            silent: true,
        });
    }

    addOrder(order: Order) : Promise<boolean> {
        return this.ajax({
            method: "POST",
            url: `api/orders`,
            data: JSON.stringify(order),
            contentType: "application/json; charset=utf-8",
        });
    }

    editOrder(order: Order) : Promise<boolean> {
        return this.ajax({
            method: "POST",
            url: `api/orders`,
            data: JSON.stringify(order),
            contentType: "application/json; charset=utf-8",
        });
    }

    syncOrders(silent: boolean) : Promise<number> {
        return this.ajax({
            method: "POST",
            url: `api/orders/sync`
        }, {
            silent: silent,
            noClearCache: true,
        });
    }

    setDeliveryDate(orderid: string, date: JsonDate | undefined) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderid}/deliveryDate`,
            data: JSON.stringify({deliveryDate: date}),
            contentType: "application/json; charset=utf-8",
        });
    }

    setDeliveryTime(orderid: string, deliveryAfter: string | undefined, deliveryBefore: string | undefined) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderid}/deliveryTime`,
            data: JSON.stringify({
                deliveryAfter: deliveryAfter,
                deliveryBefore: deliveryBefore
            }),
            contentType: "application/json; charset=utf-8",
        });
    }

    setInventoryUse(orderid: string, uses: InventoryUse[]) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderid}/inventoryUse`,
            data: JSON.stringify(uses),
            contentType: "application/json; charset=utf-8",
        });
    }

    recordStatus(orderId: String, history: OrderHistory) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderId}/history`,
            data: JSON.stringify(history),
            contentType: "application/json; charset=utf-8",
        });
    }

    deleteHistory(orderId: String, idx: number) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderId}/history/delete/${idx}`,
            contentType: "application/json; charset=utf-8",
        });
    }

    setImportant(orderId: String, important: boolean) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderId}/important`,
            data: JSON.stringify({important: important}),
            contentType: "application/json; charset=utf-8",
        });
    }

    sendSMS(orderId: string, messages: {phone: string, message: string}[]) : Promise<Order> {
        const data = {
            "messages": messages
        };
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderId}/sms`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8",
        });
    }

    getProducts() : Promise<Product[]> {
        return this.ajax({
            method: "GET",
            url: `api/products`,
            contentType: "application/json; charset=utf-8",
        });
    }

    getShopProducts() : Promise<Product[]> {
        return this.ajax({
            method: "GET",
            url: `api/products/shop`,
            contentType: "application/json; charset=utf-8",
        });
    }

    getProductsStatus(useProductId? : string) : Promise<Product[]> {
        return this.ajax({
            method: "POST",
            url: `api/products/status`,
            data: JSON.stringify({useProductId: useProductId}),
            contentType: "application/json; charset=utf-8",
        });
    }

    updateVariants(id: string, useProductId?: string) : Promise<Product> {
        return this.ajax({
            method: "POST",
            url: `api/products/updateVariants`,
            data: JSON.stringify({id: id, useProductId: useProductId}),
            contentType: "application/json; charset=utf-8"
        });
    }

    getUsers() : Promise<User[]> {
        return this.ajax({
            method: "GET",
            url: `api/users`,
            contentType: "application/json; charset=utf-8",
        });
    }

    saveUser(user: User) : Promise<User[]> {
        return this.ajax({
            method: "POST",
            url: `api/users`,
            data: JSON.stringify(user),
            contentType: "application/json; charset=utf-8"
        });
    }

    addCasualDriver(user: User) : Promise<User> {
        return this.ajax({
            method: "POST",
            url: `api/casual-driver`,
            data: JSON.stringify(user),
            contentType: "application/json; charset=utf-8"
        });
    }

    getCasualDriverLogin(username: string) : Promise<string> {
        return this.ajax({
            method: "GET",
            url: `api/casual-driver/${encodeURIComponent(username)}/login`
        });
    }

    sendCasualDriverSMS(data: {username: string, phone: string}) : Promise<boolean> {
        return this.ajax({
            method: "POST",
            url: `api/casual-driver/sms`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    giveToDriver(orderId: String, driver: string) : Promise<Order> {
        return this.ajax({
            method: "POST",
            url: `api/orders/${orderId}/giveToDriver`,
            data: JSON.stringify({driver: driver}),
            contentType: "application/json; charset=utf-8",
        });
    }

    createPaymentIntent(cents: number, description?: string | undefined) : Promise<string> {
        const data : any = {
            cents: cents
        };
        if (description) {
            data.description = description;
        }
        return this.ajax({
            method: "POST",
            url: `api/payments/intent`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    getMapsAPIKey() : Promise<string> {
        return this.getUser().then((user) => {
            return this.ajax({
                method: "GET",
                url: `api/maps/apikey`,
                contentType: "application/json; charset=utf-8"
            }, {
                noAuthRedirects: true
            });
        });
    }

    uploadFile(file: File) : Promise<Attachment> {
        const uploader = new Uploader({
            url: "api/docs",
            maxWidth: 800,
            maxHeight: 800
        }, this);
        return uploader.uploadFile(file);
    }

    recordDeliveryLoad(contents: string) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/delivery/load`,
            data: contents,
            contentType: "text/plain; charset=utf-8"
        }, {
            silent: true,
            noAuthRedirects: true,
            errorCallback: () => {
                // NOP, be silent
                return true;
            }
        });
    }

    fetchDeliveryLoad(username?: string) : Promise<any> {
        return this.ajax({
            method: "GET",
            url: `api/delivery/load` + (username ? `/${encodePathComponent(username)}` : "")
        });
    }

    clearDeliveryLoad(username?: string) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/delivery/load` + (username ? `/${encodePathComponent(username)}` : ""),
            data: "",
            contentType: "text/plain; charset=utf-8"
        });
    }

    checkDeliveryLoad() : Promise<string> {
        const ts = Date.now();
        return this.ajax({
            method: "GET",
            url: `api/delivery/load?ts=${ts}`
        }, {
            silent: true,
            noCache: true
        });
    }

    // ------------------------------

    getDeliveryReport(from: JsonDate, to: JsonDate) : Promise<any> {
        const data = {
            from: from,
            to: to
        };
        return this.ajax({
            method: "GET",
            url: `api/report/deliveries`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getDeliveryDetailsReport(from: JsonDate, to: JsonDate) : Promise<any> {
        const data = {
            from: from,
            to: to
        };
        return this.ajax({
            method: "GET",
            url: `api/report/deliveryDetails`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getWeeklySalesReport(from: JsonDate, to: JsonDate, onlineOnly: boolean) : Promise<ChartData> {
        const data : any = {
            from: from,
            to: to,
        };
        if (onlineOnly) {
            data.onlineOnly = true;
        }
        return this.ajax({
            method: "GET",
            url: `api/report/salesSummary`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getCummulativeSalesReport(from: JsonDate, to: JsonDate, type: "dollars" | "orders") : Promise<ChartData> {
        const data = {
            from: from,
            to: to,
            type: type
        };
        return this.ajax({
            method: "GET",
            url: `api/report/cummulativeSalesSummary`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getGoogleAdsData(from: JsonDate, to: JsonDate) : Promise<ChartData> {
        const data = {
            from: from,
            to: to
        };
        return this.ajax({
            method: "GET",
            url: `api/report/googleAds`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getActivity(): Promise<Activity[]> {
        return this.ajax({
            method: "GET",
            url: `api/activity`
        });
    }

    // ------------------------------

    getCheckoutParams() : Promise<Checkout> {
        return this.ajax({
            method: "GET",
            url: `api/checkout/params`,
            contentType: "application/json; charset=utf-8"
        });
    }

    setCheckoutParams(params: Checkout) : Promise<Checkout> {
        return this.ajax({
            method: "POST",
            url: `api/checkout/params`,
            data: JSON.stringify(params),
            contentType: "application/json; charset=utf-8"
        });
    }

    getDeliveryConfig() : Promise<DeliveryConfig> {
        return this.ajax({
            method: "GET",
            url: `api/delivery/config`,
            contentType: "application/json; charset=utf-8"
        });
    }

    saveDeliveryConfig(config: DeliveryConfig) : Promise<DeliveryConfig> {
        return this.ajax({
            method: "POST",
            url: `api/delivery/config`,
            data: JSON.stringify(config),
            contentType: "application/json; charset=utf-8"
        });
    }

    getSampleDeliveryCosts(config: DeliveryConfig) : Promise<any> {
        const q = paramsToQueryString({config: JSON.stringify(config)});
        return this.ajax({
            method: "GET",
            url: `api/delivery/cost/samples${q}`,
            contentType: "application/json; charset=utf-8"
        });
    }

    getDeliveryCost(address: string, config?: DeliveryConfig) : Promise<{ok: boolean, cost?: number, minCost?: number, msg?: string, endAddress?: string}> {
        const params : any = {
            to: address
        };
        if (config) {
            params.config = JSON.stringify(config);
        }
        const q = paramsToQueryString(params);
        return this.ajax({
            method: "GET",
            url: `api/delivery/cost${q}`,
            contentType: "application/json; charset=utf-8"
        });
    }

    sendInvoice(id: string, email: string) : Promise<any> {
        return this.ajax({
            method: "POST",
            data: JSON.stringify({email: email}),
            url: `api/orders/invoice/${encodeURIComponent(id)}`,
            contentType: "application/json; charset=utf-8"
        });
    }
    sendTaxInvoice(id: string, email: string) : Promise<any> {
        return this.ajax({
            method: "POST",
            data: JSON.stringify({email: email}),
            url: `api/orders/tax-invoice/${encodeURIComponent(id)}`,
            contentType: "application/json; charset=utf-8"
        });
    }

    website() : Promise<Website> {
        return this.ajax({
            method: "GET",
            url: `api/website`
        });
    }

    saveWebsite(website: Website) : Promise<Website> {
        return this.ajax({
            method: "POST",
            url: `api/website`,
            data: JSON.stringify(website),
            contentType: "application/json; charset=utf-8"
        });
    }

    websiteHistory() : Promise<WebsiteVersion[]> {
        return this.ajax({
            method: "GET",
            url: `api/website/history`
        });
    }

    websiteRestore(id: number) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/website/restore`,
            data: JSON.stringify({"id": id}),
            contentType: "application/json; charset=utf-8"
        });
    }

    template(path: string[], opts?: RequestOptions) : Promise<GrapesTemplate> {
        return this.ajax({
            method: "GET",
            url: `api/website/page/${encodeURIComponent(path.join("/'"))}/template`
        }, opts);
    }

    saveTemplate(path: string[], data: GrapesTemplate) : Promise<GrapesTemplate> {
        return this.ajax({
            method: "POST",
            url: `api/website/page/${encodeURIComponent(path.join("/'"))}/template`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    assets() : Promise<Asset[]> {
        return this.ajax({
            method: "GET",
            url: `api/website/assets`
        });
    }

    uploadAsset(file: File) : Promise<{ ["data"]: Asset[]}> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("filename", file.name);


        const out = this.ajax({
            url: "api/website/assets",
            data: formData,
            cache: false,
            contentType: false,
            processData: false,
            method: "POST",
        });

        return out;
    }

    collections() : Promise<Collection[]> {
        return this.ajax({
            method: "GET",
            url: `api/collections`
        });
    }

    collectionName(slug: string) {
        return this.ajax({
            method: "GET",
            url: `api/collections/${encodeURIComponent(slug)}`
        });
    }

    products(slug?: string) : Promise<Product[]> {
        return this.ajax({
            method: "GET",
            url: slug ? `api/products/${encodeURIComponent(slug)}` : `api/products`
        });
    }

    getProduct(name: string) : Promise<Product> {
        return this.ajax({
            method: "GET",
            url: `api/product/${encodeURIComponent(name)}`
        });
    }

    getSupressFrom() : Promise<boolean> {
        return this.ajax({
            method: "GET",
            url: `api/suppress_from`
        });
    }

    setSupressFrom(suppress: boolean) : Promise<boolean> {
        return this.ajax({
            method: "POST",
            data: JSON.stringify({"suppress_from_price": suppress}),
            url: `api/suppress_from`,
            contentType: "application/json; charset=utf-8"
        });
    }

    saveProduct(originalName: string, data: Product) : Promise<Product> {
        return this.ajax({
            method: "POST",
            url: `api/product/${encodeURIComponent(originalName)}`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    hideProducts(products: string[], hidden: boolean) : Promise<any> {
        const data : HideProducts = {
            product_names: products,
            hide: hidden
        };
        return this.ajax({
            method: "POST",
            url: `api/hide-products`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    saveCollection(data: Collection) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/collections`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    importWebsite(data: any) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/website/backup/import`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }
    importShop(data: any) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/shop/backup/import`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    rrweb(session: string) : Promise<string[]> {
        return this.ajax({
            method: "GET",
            url: `api/rrweb/${session}`,
        });
    }

    sessions(from: Date, to: Date) : Promise<Session[]> {
        return this.ajax({
            method: "GET",
            url: `api/sessions?from=${encodeURIComponent(from.getTime())}&to=${encodeURIComponent(to.getTime())}`,
        }, {
            silent: true,
            errorCallback: () => {
                // Ignore
                return true;
            }
        });
    }

    getGeneralInventory() : Promise<string[]> {
        return this.ajax({
            method: "GET",
            url: `api/inventory/general`
        }, {
            silent: true
        });
    }

    getInventory() : Promise<Inventory[]> {
        return this.ajax({
            method: "GET",
            url: `api/inventory`
        }, {
            silent: true
        });
    }

    addInventory(inv: Inventory) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/inventory`,
            data: JSON.stringify(inv),
            contentType: "application/json; charset=utf-8"
        });
    }

    deletePrimaryInventory(inv: Inventory) : Promise<any> {
        return this.ajax({
            method: "DELETE",
            url: `api/inventory`,
            data: JSON.stringify(inv),
            contentType: "application/json; charset=utf-8"
        });
    }

    deleteInventory(inv: string) : Promise<any> {
        return this.ajax({
            method: "DELETE",
            url: `api/inventory/${encodePathComponent(inv)}`
        });
    }

    handwritingWords(styleIdx: number, words: HandwrittenWord[]): Promise<HandwrittenWord[]> {
        return this.ajax({
            method: "POST",
            url: `api/handwriting/words?style=${styleIdx}`,
            data: JSON.stringify(words),
            contentType: "application/json; charset=utf-8"
        });
    }

    deleteHandwriting(styleIdx: number, words: HandwrittenWord[]) : Promise<any> {
        return this.ajax({
            method: "DELETE",
            url: `api/handwriting/words?style=${styleIdx}`,
            data: JSON.stringify(words),
            contentType: "application/json; charset=utf-8"
        });
    }

    savePrintCard(data: any) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/cardprint`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        }, {
            silent: true,
            noClearCache: true,
            errorCallback: (msg) => true
        });
    }

    getCardPrint(): Promise<any> {
        return this.ajax({
            method: "GET",
            url: `api/cardprint`
        });
    }

    // ------------------------------
    // debugging

    gc() : Promise<{max: number, used: number}> {
        return this.ajax({
            method: "POST",
            url: "api/gc",
        });
    }

    log(msg: string) : Promise<any> {
        console.log(msg);
        return this.ajax({
            method: "POST",
            url: "json/log",
            data: msg
        }, {
            silent: true
        });
    }

    trace(msg: string) {
        const stack_ = StackTrace.get();
        stack_.then((stack) => {

            for (const line of stack) {
                msg = msg + "\n  " + line.toString()
            }

            console.log("------------------------");
            console.log(msg);
            console.log("------------------------");

            this.log(msg);
        });
    }

    // ------------------------------
    // Misc.

    /**
     * Returns absolute URL, base of server app
     * This works for the web app and the mobile app.
     */
    getBaseUrl() : string {
        // @ts-ignore
        const url = window.location.href as string;

        // Remove hash part and trailing slash, any query string
        let baseUrl = url
            .replace(/#.*$/, "")
            .replace(/\?.*$/, "")
            .replace(/\/(([^\/\.]+\.[^\/\.]+))?$/, "");

        return baseUrl;
    }

    getShortBaseUrl() : Promise<string> {
        return this.runtimeParams_.then((params) => {
            return params["wrb.client.short_url"];
        });
    }

    /**
     * Wrap ajax calls so we can intercept if necessary.
     * @param settings
     */
    ajax(settings: JQuery.AjaxSettings, opts?: RequestOptions) : Promise<any> {

        const options : RequestOptions = jQuery.extend({}, this.defaultOpts, opts || {});

        // @ts-ignore
        const baseurl = this.getBaseUrl();

        const method = settings.method || "GET";
        const url = settings.url;

        const absUrl = baseurl + "/" + url;

        // Don't cache if settings.data is set (ie, query string)
        const useCache = method == "GET" && url && ! settings.data && ! options.noCache && url !== "json/ping" && url.indexOf("?") == -1;

        const cacheKey = (options.cachePrefix ? options.cachePrefix + ":" + url : url) as string;

        if (useCache) {
            // check cache
            const cached = this.cache[cacheKey];
            if (cached) {
                // this.debug(`Server cache: ${method} ${url}`);
                return cached;
            }
        }

        // @ts-ignore
        const isMobile = window.dk_mobile;
        const useOfflineCache = useCache && isMobile;

        const clearCache = (method == "POST" || method == "DELETE" || method == "PUT") && (! opts || ! opts.noClearCache);

        const cacheClearListeners = this.cacheClearListeners;

        if (clearCache) {
            this.clearCache();
        } else {
            settings.cache = false;
        }

        // this.debug(`Server fetch: ${method} ${absUrl}`);

        // const ctx = this.ctx;

        // idempotence - for payment required retries
        if (url && url.indexOf(baseurl) != 0) {
            settings.url = absUrl;
        }

        const start = Date.now();
        const cache = this.cache;
        let counter = 0;

        const popSpinner = options.silent ? () => {} : Spinner.pushSpinner();

        const p = new Promise((resolve, reject) => {

            sendData.apply(this);

            function sendData(this: Server) {

                const id = ++counter;

                settings.xhrFields = {
                    withCredentials: true
                };

                // Aggressive anti-caching to overcome Internet Explorer's aggressive caching
                settings.cache = options.browserCaching;

                const xhr = jQuery.ajax(settings);

                xhr.then((data, textStatus, response) => {
                    const delay = Date.now() - start;
                    const update = response.getResponseHeader("X-wrb-update");
                    const update0 = build_config.wrb_update;
                    if (update && update0 != update) {
                        jQuery("body").addClass("update-pending");
                        const auto = response.getResponseHeader("X-wrb-update-auto");
                        if (auto) {
                            jQuery("body").addClass("update-pending-auto");
                        }
                    }

                    if (clearCache) {
                        for (const l of cacheClearListeners) {
                            l();
                        }
                    }

                    resolve(data);
                });

                xhr.fail((data) => {

                    if (options.silent) {
                        reject("silent error");
                        return;
                    }

                    console.log("sendData fail " + id);

                    let error = undefined;
                    let reload = false;

                    console.log("Ajax fail, readyState=" + data.readyState + ", status=" + data.status + " " + data.statusText + ", URL=" + url);
                    console.log("Ajax fail data: " + inspect(data));
                    this.trace("Ajax fail trace: ");

                    if (data.readyState == 0) {

                        // Network error...
                        // Consider retrying request if its a GET and not too long has elapsed since we started.
                        const retry = method == "GET" && (Date.now() - start) < 30 * 1000 && counter < 30;

                        if (retry) {
                            setTimeout(() => {
                                console.log("Retrying... " + id);
                                sendData.apply(this);
                            }, 1000);

                            // Leave fail handler, without resolving outer promise.
                            xhr.abort();
                            return;
                        }

                        // Network error. We could put some retry logic in here
                        error = "There is a problem with the network or the server. Please try again in a few minutes";

                    } else if (data.status == 401) {
                        if (! options || ! options.noAuthRedirects) {
                            // Login required
                            reload = true;
                        }
                    } else {
                        let handled = false;
                        if (options.errorCallback && data.responseJSON) {
                            handled = options.errorCallback(data.responseJSON)
                        }
                        if (! handled) {
                            if (data.responseJSON && data.responseJSON.error) {
                                error = data.responseJSON.error;
                            } else {
                                // Unknown error
                                error = "An unknown error has ocurred...";
                            }
                        }
                    }

                    if (reload) {
                        console.log("reload...");

                        window.location.href = "#/login";
                        window.location.reload();
                    }

                    if (error) {
                        this.error(error);
                    }

                    reject(error);

                });
            }

        });

        if (useCache) {
            cache[cacheKey] = p;
        }

        p.finally(() => {
            popSpinner();
        });

        return p;
    }

    private error(msg: string[] | string) {
        // Display directly. This could be refactored out of the server class, eg via events.

        console.log("ERROR", msg);

        if (typeof msg == "string") {
            msg = [ msg ];
        }

        // Format array of messages into HTML
        const ol = jQuery("<ol>").addClass("server-error-list");
        jQuery(msg).each((idx) => {
            jQuery("<li>").text(msg[idx]).appendTo(ol);
        });

        Alerts.alert({
            messageHTML: ol[0].outerHTML
        });

    }

    qa(err: string) {
        const id = Date.now();
        this.ajax({
            method: 'POST',
            url: "api/qa/" + id,
            data: err
        }, {
            silent: true
        });
        return id;
    }

    debug(msg: any) {
        console.log(msg);
    }

    /**
     * Clear the local cache (not the offline cache)
     */
    public clearCache(ts?: number) {
        const ts2 = ts || Date.now();
        const cachesize = Object.keys(this.cache).length;

        console.log("clearCache ts=" + ts + "; cachesize=" + cachesize);

        // Crude - invalidate entire cache
        this.cache = {};

        // if we are the originator, trigger cross-tabs refresh with same value
        if (! ts) {
            window.localStorage.setItem(CLEAR_CACHE, ts2.toString());
        }

    }

    public clearCachePrefix(prefix: string) {
        console.log("clearCacheclearCachePrefix " + prefix);
        const keys = Object.keys(this.cache).filter((key) => key.indexOf(prefix) == 0);
        for (const key of keys) {
            console.log("clearCacheclearCachePrefix => " + key);
            delete this.cache[key];
        }
    }

    public clearVolatileCache() {
        this.clearCachePrefix(VOLATILE);
    }

    // ------------------------------

    /**
     * Payment nonces are transported as http headers along with the business payload in the http body.
     */
    private addPaymentNonce(settings: JQuery.AjaxSettings, paymentNonce: string | undefined) {
        if (paymentNonce == undefined) {
            return;
        }
        const headers = settings.headers || {};
        headers["X-TP-payment-nonce"] = paymentNonce;
        settings.headers = headers;
    }

    /**
     * A "dry run" can be executed by adding a special HTTP header to post requests
     */
    private dryrun(settings: JQuery.AjaxSettings) {
        const headers = settings.headers || {};
        headers["X-TP-dryrun"] = "true";
        settings.headers = headers;
    }

}

export interface PingResult {
    version?: string;
    branch?: string;
    commit?: string;
    playstoreUrl?: string;
}

function calcLocalStorageSize() {
    let size = 0;
    let cnt = 0;

    for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key != null) {
            const val = window.localStorage[key];
            cnt ++;
            if (val && val.length) {
                size += val.length;
            }
        }
    }
    return {
        size: size,
        cnt: cnt
    };
}

export function localStoragePrefix() {
    return "wrb-";
}
